import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { InputHintBehaviour, RetorikEvent } from '../../models/enums'
import type { TranslationFile } from '../../models/translationsType'
import type { AdaptiveCardChoice } from '../../store/slices/adaptiveCardSlice' 
import type { Configuration } from '../../models/types'
import type { ThemeColors } from '../../models/colorTypes'

interface ExternalHooksHandlerProps {
  instanceId?: string
}

const ExternalHooksHandler = ({ instanceId }: ExternalHooksHandlerProps): null => {
  const askedForKioskParametersOpening = useSelector((state: RootState) => state.utilsReducer.askedForKioskParametersOpening)
  const unlockClickedButton = useSelector((state: RootState) => state.utilsReducer.unlockClickedButton)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const retorikEvent = useSelector((state: RootState) => state.utilsReducer.retorikEvent)
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const isRTL = useSelector((state: RootState) => state.localeReducer.isRTL)
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const currentValue = useSelector((state: RootState) => state.adaptiveCardReducer.currentValue)
  const values = useSelector((state: RootState) => state.adaptiveCardReducer.values)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const isWidget = useSelector((state: RootState) => state.viewReducer.isWidget)
  const isTactile = useSelector((state: RootState) => state.viewReducer.isTactile)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const muted = useSelector((state: RootState) => state.speechReducer.muted)
  const loaderClosed = useSelector((state: RootState) => state.retorikReducer.loaderClosed)
  const configuration = useSelector((state: RootState) => state.retorikReducer.configuration)
  const conversationId = useSelector((state: RootState) => state.activityReducer.conversationId)
  const retrievingConversation = useSelector((state: RootState) => state.activityReducer.retrievingConversation)

  const askedForKioskParametersOpeningRef = useRef<number>(askedForKioskParametersOpening)
  const unlockClickedButtonRef = useRef<boolean>(unlockClickedButton)
  const currentSubViewRef = useRef<number>(currentSubView)
  const retorikEventRef = useRef<RetorikEvent | undefined>(retorikEvent)
  const localeRef = useRef<string>(locale)
  const isRTLRef = useRef<boolean>(isRTL)
  const translationRef = useRef<TranslationFile>(translation)
  const currentValueRef = useRef<Record<string, string | number | boolean | string[] | number[] | AdaptiveCardChoice> | undefined>(currentValue)
  const valuesRef = useRef<Record<string, string | number | boolean | string[] | number[] | AdaptiveCardChoice>>(values)
  const isMobileRef = useRef<boolean>(isMobile)
  const isWidgetRef = useRef<boolean>(isWidget)
  const isTactileRef = useRef<boolean>(isTactile)
  const themeColorsRef = useRef<ThemeColors>(themeColors)
  const activeRecognitionStateRef = useRef<number>(activeRecognitionState)
  const modeRef = useRef<number>(mode)
  const mutedRef = useRef<boolean>(muted)
  const loaderClosedRef = useRef<boolean>(loaderClosed)
  const configurationRef = useRef<Configuration>(configuration)
  const isUsedOnBorneRef = useRef<boolean | undefined>(undefined)
  const inputHintBehaviourRef = useRef<InputHintBehaviour | undefined>(undefined)
  const enableDocumentPrintingRef = useRef<boolean | undefined>(false)
  const conversationIdRef = useRef<string | null>(conversationId)
  const retrievingConversationRef = useRef<boolean>(retrievingConversation)

  const eventDispatch = (name: string, value: any): void => {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          instanceId: instanceId,
          value: value
        }
      })
    )
  }

  useEffect(() => {
    askedForKioskParametersOpeningRef.current = askedForKioskParametersOpening
    eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpening)
  }, [askedForKioskParametersOpening])

  useEffect(() => {
    unlockClickedButtonRef.current = unlockClickedButton
    eventDispatch('retorik_unlockClickedButton', unlockClickedButton)
  }, [unlockClickedButton])

  useEffect(() => {
    currentSubViewRef.current = currentSubView
    eventDispatch('retorik_currentSubView', currentSubView)
  }, [currentSubView])

  useEffect(() => {
    retorikEventRef.current = retorikEvent
    eventDispatch('retorik_retorikEvent', retorikEvent)
  }, [retorikEvent])

  useEffect(() => {
    localeRef.current = locale
    eventDispatch('retorik_locale', locale)
  }, [locale])

  useEffect(() => {
    isRTLRef.current = isRTL
    eventDispatch('retorik_isRTL', isRTL)
  }, [isRTL])

  useEffect(() => {
    translationRef.current = translation
    eventDispatch('retorik_translation', translation)
  }, [translation])

  useEffect(() => {
    currentValueRef.current = currentValue
    eventDispatch('retorik_currentValue', currentValue)
  }, [currentValue])

  useEffect(() => {
    valuesRef.current = values
    eventDispatch('retorik_values', values)
  }, [values])

  useEffect(() => {
    isMobileRef.current = isMobile
    eventDispatch('retorik_isMobile', isMobile)
  }, [isMobile])

  useEffect(() => {
    isWidgetRef.current = isWidget
    eventDispatch('retorik_isWidget', isWidget)
  }, [isWidget])

  useEffect(() => {
    isTactileRef.current = isTactile
    eventDispatch('retorik_isTactile', isTactile)
  }, [isTactile])

  useEffect(() => {
    themeColorsRef.current = themeColors
    eventDispatch('retorik_themeColors', themeColors)
  }, [themeColors])

  useEffect(() => {
    activeRecognitionStateRef.current = activeRecognitionState
    eventDispatch('retorik_activeRecognitionState', activeRecognitionState)
  }, [activeRecognitionState])

  useEffect(() => {
    modeRef.current = mode
    eventDispatch('retorik_mode', mode)
  }, [mode])

  useEffect(() => {
    mutedRef.current = muted
    eventDispatch('retorik_muted', muted)
  }, [muted])

  useEffect(() => {
    loaderClosedRef.current = loaderClosed
    eventDispatch('retorik_loaderClosed', loaderClosed)
  }, [loaderClosed])

  useEffect(() => {
    configurationRef.current = configuration
    eventDispatch('retorik_configuration', configuration)
  }, [configuration])

  useEffect(() => {
    isUsedOnBorneRef.current = configuration.isUsedOnBorne
    eventDispatch('retorik_isUsedOnBorne', configuration.isUsedOnBorne)
  }, [configuration.isUsedOnBorne])

  useEffect(() => {
    inputHintBehaviourRef.current = configuration.speechRecognitionOptions?.inputHintBehaviour
    eventDispatch('retorik_inputHintBehaviour', configuration.speechRecognitionOptions?.inputHintBehaviour)
  }, [configuration.speechRecognitionOptions?.inputHintBehaviour])

  useEffect(() => {
    enableDocumentPrintingRef.current = configuration.enableDocumentPrinting
    eventDispatch('retorik_enableDocumentPrinting', configuration.enableDocumentPrinting)
  }, [configuration.enableDocumentPrinting])

  useEffect(() => {
    conversationIdRef.current = conversationId
    eventDispatch('retorik_conversationId', conversationId)
  }, [conversationId])

  useEffect(() => {
    retrievingConversationRef.current = retrievingConversation
    eventDispatch('retorik_retrievingConversation', retrievingConversation)
  }, [retrievingConversation])

  useEffect(() => {
    document.addEventListener('retorik_call_askedForKioskParametersOpening', () => eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpeningRef.current))
    document.addEventListener('retorik_call_unlockClickedButton', () => eventDispatch('retorik_unlockClickedButton', unlockClickedButtonRef.current))
    document.addEventListener('retorik_call_currentSubView', () => eventDispatch('retorik_currentSubView', currentSubViewRef.current))
    document.addEventListener('retorik_call_retorikEvent', () => eventDispatch('retorik_retorikEvent', retorikEventRef.current))
    document.addEventListener('retorik_call_locale', () => eventDispatch('retorik_locale', localeRef.current))
    document.addEventListener('retorik_call_isRTL', () => eventDispatch('retorik_isRTL', isRTLRef.current))
    document.addEventListener('retorik_call_translation', () => eventDispatch('retorik_translation', translationRef.current))
    document.addEventListener('retorik_call_currentValue', () => eventDispatch('retorik_currentValue', currentValueRef.current))
    document.addEventListener('retorik_call_values', () => eventDispatch('retorik_values', valuesRef.current))
    document.addEventListener('retorik_call_isMobile', () => eventDispatch('retorik_isMobile', isMobileRef.current))
    document.addEventListener('retorik_call_isWidget', () => eventDispatch('retorik_isWidget', isWidgetRef.current))
    document.addEventListener('retorik_call_isTactile', () => eventDispatch('retorik_isTactile', isTactileRef.current))
    document.addEventListener('retorik_call_themeColors', () => eventDispatch('retorik_themeColors', themeColorsRef.current))
    document.addEventListener('retorik_call_activeRecognitionState', () => eventDispatch('retorik_activeRecognitionState', activeRecognitionStateRef.current))
    document.addEventListener('retorik_call_mode', () => eventDispatch('retorik_mode', modeRef.current))
    document.addEventListener('retorik_call_muted', () => eventDispatch('retorik_muted', mutedRef.current))
    document.addEventListener('retorik_call_loaderClosed', () => eventDispatch('retorik_loaderClosed', loaderClosedRef.current))
    document.addEventListener('retorik_call_configuration', () => eventDispatch('retorik_configuration', configurationRef.current))
    document.addEventListener('retorik_call_isUsedOnBorne', () => eventDispatch('retorik_isUsedOnBorne', isUsedOnBorneRef.current))
    document.addEventListener('retorik_call_inputHintBehaviour', () => eventDispatch('retorik_inputHintBehaviour', inputHintBehaviourRef.current))
    document.addEventListener('retorik_call_enableDocumentPrinting', () => eventDispatch('retorik_enableDocumentPrinting', enableDocumentPrintingRef.current))
    document.addEventListener('retorik_call_conversationId', () => eventDispatch('retorik_conversationId', conversationIdRef.current))
    document.addEventListener('retorik_call_retrievingConversation', () => eventDispatch('retorik_retrievingConversation', retrievingConversationRef.current))

    return () => {
      document.removeEventListener('retorik_call_askedForKioskParametersOpening', () => eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpeningRef.current))
      document.removeEventListener('retorik_call_unlockClickedButton', () => eventDispatch('retorik_unlockClickedButton', unlockClickedButtonRef.current))
      document.removeEventListener('retorik_call_currentSubView', () => eventDispatch('retorik_currentSubView', currentSubViewRef.current))
      document.removeEventListener('retorik_call_retorikEvent', () => eventDispatch('retorik_retorikEvent', retorikEventRef.current))
      document.removeEventListener('retorik_call_locale', () => eventDispatch('retorik_locale', localeRef.current))
      document.removeEventListener('retorik_call_isRTL', () => eventDispatch('retorik_isRTL', isRTLRef.current))
      document.removeEventListener('retorik_call_translation', () => eventDispatch('retorik_translation', translationRef.current))
      document.removeEventListener('retorik_call_currentValue', () => eventDispatch('retorik_currentValue', currentValueRef.current))
      document.removeEventListener('retorik_call_values', () => eventDispatch('retorik_values', valuesRef.current))
      document.removeEventListener('retorik_call_isMobile', () => eventDispatch('retorik_isMobile', isMobileRef.current))
      document.removeEventListener('retorik_call_isWidget', () => eventDispatch('retorik_isWidget', isWidgetRef.current))
      document.removeEventListener('retorik_call_isTactile', () => eventDispatch('retorik_isTactile', isTactileRef.current))
      document.removeEventListener('retorik_call_themeColors', () => eventDispatch('retorik_themeColors', themeColorsRef.current))
      document.removeEventListener('retorik_call_activeRecognitionState', () => eventDispatch('retorik_activeRecognitionState', activeRecognitionStateRef.current))
      document.removeEventListener('retorik_call_mode', () => eventDispatch('retorik_mode', modeRef.current))
      document.removeEventListener('retorik_call_muted', () => eventDispatch('retorik_muted', mutedRef.current))
      document.removeEventListener('retorik_call_loaderClosed', () => eventDispatch('retorik_loaderClosed', loaderClosedRef.current))
      document.removeEventListener('retorik_call_configuration', () => eventDispatch('retorik_configuration', configurationRef.current))
      document.removeEventListener('retorik_call_isUsedOnBorne', () => eventDispatch('retorik_isUsedOnBorne', isUsedOnBorneRef.current))
      document.removeEventListener('retorik_call_inputHintBehaviour', () => eventDispatch('retorik_inputHintBehaviour', inputHintBehaviourRef.current))
      document.removeEventListener('retorik_call_enableDocumentPrinting', () => eventDispatch('retorik_enableDocumentPrinting', enableDocumentPrintingRef.current))
      document.removeEventListener('retorik_call_conversationId', () => eventDispatch('retorik_conversationId', conversationIdRef.current))
      document.removeEventListener('retorik_call_retrievingConversation', () => eventDispatch('retorik_retrievingConversation', retrievingConversationRef.current))
    }
  }, [])

  return null
}

export default ExternalHooksHandler
